import { Input, Table, TableColumnsType, TablePaginationConfig, Tag } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { Search } from 'lucide-react';
import Image from 'next/image';

import { ButtonCopy, TimeRow } from '@components/shared';
import { ESortOrder, EUnitDisplay, IProduct } from '@type/product';
import { formatVND } from '@utils/number';

import { useProductPriceProposalTable } from './hooks/useProductPriceProposalTable';

export interface IProductCustomProposalPrice {
  _id?: string;
  customPrice?: number;
  productId?: string;
  userId?: string;
  product: IProduct;
}

export default function ProductPriceProposalProposalTable({ userId }: { userId: string }) {
  const { productPriceProposal, totalData, filters, setFilters, search, setSearch } =
    useProductPriceProposalTable(userId);

  const columns: TableColumnsType<IProductCustomProposalPrice> = [
    {
      title: 'Thông tin sản phẩm',
      dataIndex: 'product',
      render: (product: IProduct) => (
        <div className="flex items-center gap-2">
          <Image
            src={product?.images?.[0] || '/img/image_empty.svg'}
            alt=""
            width={40}
            height={40}
            className="rounded"
          />
          <div className="text-start flex-1">
            <div className="text-sm font-semibold text-secondary line-clamp-2">
              {product?.name} <span className="text-main">({EUnitDisplay[product?.unitName]})</span>
            </div>
            <ButtonCopy text={product?.code} className="text-xs font-medium text-gray_600">
              ID: {product?.code}
            </ButtonCopy>
          </div>
        </div>
      ),
      width: '20%',
    },
    {
      title: 'Giá thỏa thuận',
      key: 'customPrice',
      dataIndex: 'customPrice',
      align: 'center',
      sorter: true,
      render: (customPrice, record) => (
        <Tag color="blue" className="font-semibold text-sm">
          {formatVND(customPrice || 0)}{' '}
          <span className="text-xs text-gray-600">đ/{EUnitDisplay[record?.product?.unitName]}</span>
        </Tag>
      ),
      width: '15%',
    },
    {
      title: 'Giá sau thuế',
      key: 'customPrice',
      dataIndex: 'customPrice',
      align: 'center',
      sorter: true,
      render: (customPrice, record) => (
        <Tag color="blue" className="font-semibold text-sm">
          {formatVND(customPrice + (customPrice * record?.product?.tax) / 100 || 0)}{' '}
          <span className="text-xs text-gray-600">đ/{EUnitDisplay[record?.product?.unitName]}</span>
        </Tag>
      ),
      width: '15%',
    },
    {
      title: 'Ngày đề xuất',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      sorter: true,
      render: (value) => <TimeRow time={value} />,
      width: '10%',
    },
  ];

  const onChangePagination = (
    pagination: TablePaginationConfig,
    _,
    sorter: SorterResult<IProductCustomProposalPrice>
  ) => {
    setFilters((prev) => ({
      ...prev,
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      sort: sorter?.field as string,
      order: sorter?.order === 'ascend' ? ESortOrder.Asc : ESortOrder.Desc,
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-sm h-10"
          placeholder="Tìm kiếm sản phẩm, mã sản phẩm"
          prefix={<Search className="text-orange-600" />}
        />
      </div>

      <Table
        className="border border-gray-200 rounded-md"
        onChange={onChangePagination}
        scroll={{ x: 800 }}
        pagination={{
          pageSize: filters.limit,
          current: filters.page,
          total: totalData,
          showSizeChanger: false,
          position: ['bottomCenter'],
        }}
        columns={columns}
        dataSource={productPriceProposal}
      />
    </div>
  );
}
